import { GridFilterModel } from '@mui/x-data-grid';
import { createContext, FunctionComponent, PropsWithChildren, useContext, useState } from 'react';

type Filterable = {
	isActive: boolean;
	tags: string[];
	authorName: string;
} & ({ text: string; } | { name: string; });

type ContextValue = {
	applyFilters: <T extends Filterable>(items: T[]) => T[];
	clearFilters: () => void;
	filterActive: boolean | undefined;
	setFilterActive: (value: boolean | undefined) => void;
	filterTags: string[];
	setFilterTags: (value: string[]) => void;
	filterAuthor: string;
	setFilterAuthor: (value: string) => void;
	searchText: string;
	setSearchText: (value: string) => void;
	gridFilterModel: GridFilterModel;
	setGridFilterModel: (value: GridFilterModel) => void;
};

const FilterContext = createContext<ContextValue | undefined>(undefined);

export const QuestionFiltersProvider: FunctionComponent<PropsWithChildren> = (props) => {
	const [filterActive, setFilterActive] = useState<boolean | undefined>();
	const [filterTags, setFilterTags] = useState<string[]>([]);
	const [filterAuthor, setFilterAuthor] = useState<string>('');
	const [searchText, setSearchText] = useState<string>('');
	const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({ items: [] });

	function applyFilters<T extends Filterable>(items: T[]): T[] {
		return items.filter(item => {
			const searchableText = ('text' in item) ? item.text : item.name;
			const searchLower = searchText.toLowerCase();
			const matchesSearch = !searchText ||
				searchableText.toLowerCase().includes(searchLower) ||
				item.tags.some(tag => tag.toLowerCase().includes(searchLower));
			return ((filterActive === undefined) || (item.isActive === filterActive))
				&& (filterTags.empty() || filterTags.some(tag => item.tags.includes(tag)))
				&& (!filterAuthor || (item.authorName === filterAuthor))
				&& matchesSearch;
		});
	}

	function clearFilters() {
		setFilterActive(undefined);
		setFilterTags([]);
		setFilterAuthor('');
		setSearchText('');
		setGridFilterModel({ items: [] });
	}

	return <FilterContext.Provider value={{
		applyFilters,
		clearFilters,
		filterActive, setFilterActive,
		filterTags, setFilterTags,
		filterAuthor, setFilterAuthor,
		searchText, setSearchText,
		gridFilterModel, setGridFilterModel,
	}}>
		{props.children}
	</FilterContext.Provider>;
};

export function useQuestionFilters(): ContextValue {
	const context = useContext(FilterContext);
	if (!context) {
		throw new Error('useQuestionFilters must be used within QuestionFiltersProvider');
	}
	return context;
}
